<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentDisplayName = ref('');
const agentEmail = ref('');
const agentWhatsapp = ref('');
const agentMessageSignature = ref('');
const agentAvailability = ref('offline');
const autoOffline = ref(true);
const selectedRoleId = ref('agent');

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  selectedRoleId,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
    {
      id: 'captador',
      name: 'captador',
      label: t('AGENT_MGMT.AGENT_TYPES.CAPTADOR'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      name: agentName.value,
      display_name: agentDisplayName.value,
      email: agentEmail.value,
      availability: agentAvailability.value,
      auto_offline: autoOffline.value,
      message_signature: agentMessageSignature.value,
    };
    const whatsappPhone = agentWhatsapp.value.trim();
    if (whatsappPhone) {
      payload.custom_attributes = {
        manager_phone_e164: whatsappPhone,
        phone_e164: whatsappPhone,
        whatsapp: whatsappPhone,
      };
    }

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
    }

    await store.dispatch('agents/create', payload);
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col overflow-auto bg-white">
    <header class="border-b border-[#eee5d6] px-6 py-5">
      <h2 class="font-redHatDisplay text-[18px] font-semibold text-[#1a1a1a]">
        {{ $t('AGENT_MGMT.ADD.TITLE') }}
      </h2>
      <p class="mt-2 text-[14px] leading-6 text-[#667085]">
        {{ $t('AGENT_MGMT.ADD.DESC') }}
      </p>
    </header>
    <form class="flex flex-col gap-5 px-6 py-6" @submit.prevent="addAgent">
      <div class="w-full">
        <label
          class="mb-2 block text-[13px] font-semibold text-[#1a1a1a]"
          :class="{ '!text-[#e5646f]': v$.agentName.$error }"
        >
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
        </label>
        <input
          v-model="agentName"
          type="text"
          class="h-11 w-full rounded-lais border px-3 text-[14px] text-[#1a1a1a] outline-none transition placeholder:text-[#98a2b3] focus:border-[#49317a]"
          :class="v$.agentName.$error ? 'border-[#e5646f]' : 'border-[#d9d2e6]'"
          :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
          @input="v$.agentName.$touch"
        />
      </div>

      <div class="w-full">
        <label class="mb-2 block text-[13px] font-semibold text-[#1a1a1a]">
          {{ $t('AGENT_MGMT.ADD.FORM.DISPLAY_NAME.LABEL') }}
        </label>
        <input
          v-model="agentDisplayName"
          type="text"
          class="h-11 w-full rounded-lais border border-[#d9d2e6] px-3 text-[14px] text-[#1a1a1a] outline-none transition placeholder:text-[#98a2b3] focus:border-[#49317a]"
          :placeholder="$t('AGENT_MGMT.ADD.FORM.DISPLAY_NAME.PLACEHOLDER')"
        />
      </div>

      <div class="w-full">
        <label
          class="mb-2 block text-[13px] font-semibold text-[#1a1a1a]"
          :class="{ '!text-[#e5646f]': v$.selectedRoleId.$error }"
        >
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
        </label>
        <select
          v-model="selectedRoleId"
          class="h-11 w-full rounded-lais border px-3 text-[14px] text-[#1a1a1a] outline-none transition focus:border-[#49317a]"
          :class="
            v$.selectedRoleId.$error ? 'border-[#e5646f]' : 'border-[#d9d2e6]'
          "
          @change="v$.selectedRoleId.$touch"
        >
          <option v-for="role in roles" :key="role.id" :value="role.id">
            {{ role.label }}
          </option>
        </select>
        <span
          v-if="v$.selectedRoleId.$error"
          class="mt-2 block text-[12px] text-[#e5646f]"
        >
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
        </span>
      </div>

      <div class="w-full">
        <label
          class="mb-2 block text-[13px] font-semibold text-[#1a1a1a]"
          :class="{ '!text-[#e5646f]': v$.agentEmail.$error }"
        >
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
        </label>
        <input
          v-model="agentEmail"
          type="email"
          class="h-11 w-full rounded-lais border px-3 text-[14px] text-[#1a1a1a] outline-none transition placeholder:text-[#98a2b3] focus:border-[#49317a]"
          :class="
            v$.agentEmail.$error ? 'border-[#e5646f]' : 'border-[#d9d2e6]'
          "
          :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
          @input="v$.agentEmail.$touch"
        />
      </div>

      <div class="w-full">
        <label class="mb-2 block text-[13px] font-semibold text-[#1a1a1a]">
          {{ $t('AGENT_MGMT.ADD.FORM.WHATSAPP.LABEL') }}
        </label>
        <input
          v-model="agentWhatsapp"
          type="tel"
          class="h-11 w-full rounded-lais border border-[#d9d2e6] px-3 text-[14px] text-[#1a1a1a] outline-none transition placeholder:text-[#98a2b3] focus:border-[#49317a]"
          :placeholder="$t('AGENT_MGMT.ADD.FORM.WHATSAPP.PLACEHOLDER')"
        />
      </div>

      <div class="grid gap-5 sm:grid-cols-2">
        <div class="w-full">
          <label class="mb-2 block text-[13px] font-semibold text-[#1a1a1a]">
            {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          </label>
          <select
            v-model="agentAvailability"
            class="h-11 w-full rounded-lais border border-[#d9d2e6] px-3 text-[14px] text-[#1a1a1a] outline-none transition focus:border-[#49317a]"
          >
            <option value="online">
              {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE') }}
            </option>
            <option value="busy">
              {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY') }}
            </option>
            <option value="offline">
              {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE') }}
            </option>
          </select>
        </div>
        <label
          class="flex min-h-11 items-center gap-3 rounded-lais border border-[#d9d2e6] px-3 text-[13px] font-semibold text-[#1a1a1a]"
        >
          <input
            v-model="autoOffline"
            type="checkbox"
            class="size-4 accent-[#49317a]"
          />
          {{ $t('AGENT_MGMT.ADD.FORM.AUTO_OFFLINE.LABEL') }}
        </label>
      </div>

      <div class="w-full">
        <label class="mb-2 block text-[13px] font-semibold text-[#1a1a1a]">
          {{ $t('AGENT_MGMT.ADD.FORM.MESSAGE_SIGNATURE.LABEL') }}
        </label>
        <textarea
          v-model="agentMessageSignature"
          rows="3"
          class="w-full rounded-lais border border-[#d9d2e6] px-3 py-2 text-[14px] text-[#1a1a1a] outline-none transition placeholder:text-[#98a2b3] focus:border-[#49317a]"
          :placeholder="$t('AGENT_MGMT.ADD.FORM.MESSAGE_SIGNATURE.PLACEHOLDER')"
        />
      </div>

      <div
        class="flex flex-col-reverse gap-3 border-t border-[#f1ebf8] pt-5 sm:flex-row sm:justify-end"
      >
        <button
          type="button"
          class="inline-flex h-10 items-center justify-center rounded-lais border border-[#d9d2e6] px-4 text-[13px] font-semibold text-[#667085] transition hover:bg-[#f7f4fb]"
          @click.prevent="emit('close')"
        >
          {{ $t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT') }}
        </button>
        <button
          type="submit"
          class="inline-flex h-10 items-center justify-center gap-2 rounded-lais bg-[#49317a] px-4 text-[13px] font-semibold text-white transition hover:bg-[#3d2966] disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="v$.$invalid || uiFlags.isCreating"
        >
          <span
            v-if="uiFlags.isCreating"
            class="i-lucide-loader-circle size-4 animate-spin"
            aria-hidden="true"
          />
          {{ $t('AGENT_MGMT.ADD.FORM.SUBMIT') }}
        </button>
      </div>
    </form>
  </div>
</template>
